import React from 'react'
import type { Report, ReportCategory } from './types'

interface Props {
  categories: ReportCategory[]
  reports: Report[]
  selectedCategory: string
  selectedReport: Report | null
  onSelectCategory: (id: string) => void
  onSelectReport: (report: Report) => void
}

export const ReportSidebar: React.FC<Props> = ({
  categories,
  reports,
  selectedCategory,
  selectedReport,
  onSelectCategory,
  onSelectReport,
}) => {
  const filtered = selectedCategory === 'all'
    ? reports
    : reports.filter(r => r.category === selectedCategory)

  return (
    <aside className="flex flex-col gap-4 w-full lg:w-72 flex-shrink-0">
      {/* Category pills */}
      <nav className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3">
          Categories
        </h2>
        <ul className="space-y-1">
          {categories.map(cat => {
            const Icon = cat.icon
            const active = selectedCategory === cat.id
            return (
              <li key={cat.id}>
                <button
                  onClick={() => onSelectCategory(cat.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors text-left ${
                    active
                      ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 font-semibold'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50'
                  }`}
                >
                  <Icon size={16} />
                  {cat.name}
                </button>
              </li>
            )
          })}
        </ul>
      </nav>

      {/* Report list */}
      <nav className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 flex-1 overflow-y-auto">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3">
          Reports
        </h2>
        {filtered.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">No reports in this category</p>
        ) : (
          <ul className="space-y-1">
            {filtered.map(report => {
              const Icon = report.icon
              const active = selectedReport?.id === report.id
              return (
                <li key={report.id}>
                  <button
                    onClick={() => onSelectReport(report)}
                    className={`w-full flex items-start gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors text-left ${
                      active
                        ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50'
                    }`}
                  >
                    <Icon size={15} className="mt-0.5 flex-shrink-0" />
                    <span className={`leading-tight ${active ? 'font-semibold' : ''}`}>{report.name}</span>
                  </button>
                </li>
              )
            })}
          </ul>
        )}
      </nav>
    </aside>
  )
}
